import { Gate } from './Gate';
import { Logger } from './Logger';
import { wait } from './wait';
import { TimeoutError, withTimeout } from './withTimeout';
/**
 * Query one entity with queryEntity method, and a list of entitities with queryEntities method.
 * The queryEntities method does pagination automatically.
 *
 * If we want to ensure that The Graph index is up-to-date, we can call the updateRequiredBlockNumber()
 * method. In that case a queryEntity()/queryEntities() waits until The Graph has been indexed at least
 * to that block number. If the indexing takes longer than opts.indexTimeout, the query call rejects
 * with a TimeoutError.
 */
export class TheGraphClient {
    requiredBlockNumber = 0;
    indexingState;
    serverUrl;
    fetch;
    logger;
    constructor(opts) {
        this.serverUrl = opts.serverUrl;
        this.fetch = opts.fetch;
        this.logger = opts.logger ?? new Logger('TheGraphClient');
        this.indexingState = new IndexingState(() => this.getIndexBlockNumber(), opts.indexTimeout ?? 60000, opts.indexPollInterval ?? 1000, this.logger);
    }
    async queryEntity(query) {
        await this.indexingState.waitUntilIndexed(this.requiredBlockNumber);
        return this.sendQuery(query);
    }
    async *queryEntities(createQuery, 
    /*
     * For simple queries there is one root level property, e.g. "streams" or "permissions"
     * which contain array of items. If the query contains more than one root level property
     * or we want to return non-root elements as items, the caller must pass a custom
     * function to parse the items.
     */
    parseItems = (response) => {
        const rootKey = Object.keys(response)[0];
        return response[rootKey];
    }, pageSize = 1000) {
        await this.indexingState.waitUntilIndexed(this.requiredBlockNumber);
        let lastResultSet;
        do {
            const lastId = (lastResultSet !== undefined) ? lastResultSet[lastResultSet.length - 1].id : '';
            const query = createQuery(lastId, pageSize, this.requiredBlockNumber);
            const response = await this.sendQuery(query);
            const items = parseItems(response);
            yield* items;
            lastResultSet = items;
        } while (lastResultSet.length === pageSize);
    }
    updateRequiredBlockNumber(blockNumber) {
        this.requiredBlockNumber = Math.max(blockNumber, this.requiredBlockNumber);
    }
    async sendQuery(query) {
        this.logger.trace('Send GraphQL query', { query });
        const res = await this.fetch(this.serverUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                accept: '*/*',
            },
            body: JSON.stringify(query)
        });
        const resText = await res.text();
        let resJson;
        try {
            resJson = JSON.parse(resText);
        }
        catch {
            throw new Error(`GraphQL query failed with "${resText}"`);
        }
        this.logger.trace('Received GraphQL response', { resJson });
        if (!resJson.data) {
            if (resJson.errors && resJson.errors.length > 0) {
                throw new Error('GraphQL query failed: ' + JSON.stringify(resJson.errors.map((e) => e.message)));
            }
            else {
                throw new Error('GraphQL query failed');
            }
        }
        return resJson.data;
    }
    async getIndexBlockNumber() {
        const response = await this.sendQuery({ query: '{ _meta { block { number } } }' });
        // eslint-disable-next-line no-underscore-dangle
        return response._meta.block.number;
    }
}
class BlockNumberGate extends Gate {
    blockNumber;
    constructor(blockNumber) {
        super(true);
        this.blockNumber = blockNumber;
    }
}
class IndexingState {
    blockNumber = 0;
    gates = new Set();
    getCurrentBlockNumber;
    timeout;
    pollInterval;
    logger;
    constructor(getCurrentBlockNumber, timeout, pollInterval, logger) {
        this.getCurrentBlockNumber = async () => {
            try {
                return await getCurrentBlockNumber();
            }
            catch (err) {
                logger.warn('Failed to get current block number', { reason: err instanceof Error ? err.message : String(err) });
                return undefined;
            }
        };
        this.timeout = timeout;
        this.pollInterval = pollInterval;
        this.logger = logger;
    }
    async waitUntilIndexed(blockNumber) {
        if (blockNumber <= this.blockNumber) {
            return;
        }
        this.logger.debug('Wait until The Graph is synchronized', { blockNumber: this.blockNumber, blockTarget: blockNumber });
        const gate = this.getOrCreateGate(blockNumber);
        try {
            await withTimeout(gate.waitUntilOpen(), this.timeout, `The Graph did not synchronize to block ${blockNumber}`);
        }
        catch (e) {
            if (e instanceof TimeoutError) {
                this.gates.delete(gate);
            }
            throw e;
        }
    }
    getOrCreateGate(blockNumber) {
        const gate = new BlockNumberGate(blockNumber);
        if (blockNumber > this.blockNumber) {
            const isPolling = this.gates.size > 0;
            gate.close();
            this.gates.add(gate);
            if (!isPolling) {
                this.startPolling();
            }
        }
        return gate;
    }
    async startPolling() {
        this.logger.trace('Start polling');
        while (this.gates.size > 0) {
            const newBlockNumber = await this.getCurrentBlockNumber();
            if (newBlockNumber !== undefined && newBlockNumber !== this.blockNumber) {
                this.blockNumber = newBlockNumber;
                this.logger.trace('Polled', { blockNumber: this.blockNumber });
                this.gates.forEach((gate) => {
                    if (gate.blockNumber <= this.blockNumber) {
                        gate.open();
                        this.gates.delete(gate);
                    }
                });
            }
            if (this.gates.size > 0) {
                await wait(this.pollInterval);
            }
        }
        this.logger.trace('Stop polling');
    }
}
